import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Clock, ArrowRight, HandHeart, ChatCircleDots, Lock, Calendar, Database, ChartLineUp, Plugs, Users, Sparkle } from "@phosphor-icons/react";
import FeedbackCTA from "../components/FeedbackCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREE_BULLETS = [
  "Watch a handful of items, daily, across PB Tech, JB Hi-Fi and Harvey Norman",
  "Price-drop and sale alerts by email when a watched item moves",
  "Promo codes spotted on the page surfaced on your dashboard",
  "Bonus offer alerts when a free item, store credit or cashback appears",
  "Smart cross-retailer match suggestions, so you spot the same product cheaper",
  "One refund-claim a year on the house - real money back, drafted for you",
];

const PRO_BULLETS = [
  "Watch everything you own and want, without limits",
  "Instant alerts on price drops, sales, promo codes and bonus offers",
  "File price adjustment claims with one click - drafted, sent, and tracked for you",
  "Your receipts, safe and searchable forever, read automatically as they arrive",
  "Warranty cover quietly tracked in the background, with a nudge before each one expires",
  "An AI that writes the awkward emails for you, grounded in NZ consumer law",
  "Insurance-ready asset exports and a year-in-review snapshot of every dollar saved",
  "Priority support - a real Kiwi reads every message",
];

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState("");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    axios.get(`${API}/billing/plans`).then((r) => setPlans(r.data)).catch(() => {});
    if (params.get("cancelled")) toast.info("Checkout cancelled - no charge made.");
  }, [params]);

  // React Router doesn't auto-scroll to `#hash` targets, so we handle the
  // deep-link from the Dashboard B2B strip and the landing teaser ourselves.
  // Slight timeout lets the page render before we measure scroll position.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [location.hash]);

  const launchFree = plans?.launch_free === true;

  const handleUpgrade = async (planId) => {
    if (!user) {
      navigate(`/login?next=/pricing`);
      return;
    }
    setBusy(planId);
    try {
      const token = localStorage.getItem("pg_token");
      const r = await axios.post(
        `${API}/billing/checkout`,
        { plan_id: planId, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Could not start checkout. Try again in a moment.");
        setBusy("");
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) toast.error("Stripe is temporarily unavailable - please try again in a moment.");
      else toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setBusy("");
    }
  };

  const fmt = (n) => `$${Number(n).toFixed(2).replace(/\.00$/, "")}`;

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <Header />
      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-2xl">
          <div className="pg-overline mb-3">Pricing · all in NZD</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
            One save pays for itself<span className="text-[#002FA7]">.</span>
          </h1>
          <p className="text-[#52525b] text-base sm:text-lg leading-relaxed mt-6">
            Pro is $4.99 a month, or $49 a year. One $50 win - a wishlist drop you caught before paying full price, a price-adjustment refund after you bought, a warranty repair you wouldn't have known to ask for - covers Pro for a full year. Free includes a 5-item wishlist and one free price-adjustment claim a year, so you can try it before you ever pay.
          </p>
          <p className="text-[#52525b] text-sm leading-relaxed mt-3">
            And right now? Pro is free for everyone during our launch window.
          </p>

          {/* ROI ANCHOR — concrete examples across all three pillars
              (before-buy discount alert, after-buy refund, warranty
              outcome) so the value picture isn't pinned to a single
              price-protection moment. */}
          <div className="mt-6 border border-[#e4e4e7] bg-[#f7f7f8] p-5 flex items-start gap-4" data-testid="pricing-roi-anchor">
            <div className="flex-shrink-0 w-12 h-12 bg-white border border-[#e4e4e7] flex items-center justify-center">
              <span className="font-heading font-black text-xl text-[#16A34A]">$</span>
            </div>
            <div className="text-sm text-[#0a0a0a] leading-relaxed">
              <strong>Three ways Pro pays itself off.</strong> Catch a $60 wishlist kettle on sale before you click buy. Recover $180 on a laptop with a price-adjustment claim a week after you bought it. Get a warranty repair on a fridge two years in because we kept the receipt and drafted the CGA email. Any one of these covers a year of Pro. The receipt vault, warranty alerts, and AI claim drafts come standard.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mt-12">
          {/* FREE */}
          <div className="bg-white p-8" data-testid="pricing-free">
            <div className="pg-overline">Free · no card needed</div>
            <h2 className="font-heading text-2xl font-extrabold mt-3 tracking-tight">Household starter</h2>
            <div className="font-heading text-5xl font-black mt-6 tracking-tighter">$0</div>
            <p className="font-mono-pg text-xs text-[#52525b] mt-1 uppercase tracking-widest">5 wishlist items · 1 free claim a year</p>
            <ul className="mt-7 space-y-3 text-sm">
              {FREE_BULLETS.map((b) => (
                <li key={b} className="flex gap-2"><ShieldCheck size={18} color="#16A34A" weight="fill" className="shrink-0 mt-0.5" /> {b}</li>
              ))}
            </ul>
            {!user ? (
              <Link to="/signup" className="mt-8 inline-flex w-full justify-center bg-[#002FA7] text-white font-bold px-6 py-3 hover:bg-[#00207A]" data-testid="pricing-free-cta">
                {launchFree ? "Sign up - Pro included free" : "Get started - free"}
              </Link>
            ) : (
              <Link to="/dashboard" className="mt-8 inline-flex w-full justify-center border border-[#0a0a0a] text-[#0a0a0a] font-bold px-6 py-3 hover:bg-[#0a0a0a] hover:text-white transition-colors" data-testid="pricing-free-dashboard">
                Go to dashboard <ArrowRight size={16} weight="bold" className="ml-2" />
              </Link>
            )}
          </div>

          {/* PRO */}
          <div className="bg-[#0a0a0a] text-white p-8 relative" data-testid="pricing-pro">
            <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 bg-[#D97706] text-white text-[10px] font-bold tracking-widest uppercase px-2.5 py-1">
              {launchFree ? "Free during launch" : "Most value"}
            </div>
            <div className="pg-overline text-white/60">Pro household</div>
            <h2 className="font-heading text-2xl font-extrabold mt-3 tracking-tight">Pro</h2>

            {launchFree ? (
              <div className="mt-6 border border-white/15 bg-white/5 p-5" data-testid="pricing-launch-free-notice">
                <div className="font-heading text-3xl font-black tracking-tighter">$0 right now<span className="text-white/60">.</span></div>
                <p className="text-sm text-white/70 mt-3 leading-relaxed">
                  Pro features are free for every Kiwi while we polish the launch. When paid plans switch on, we'll email you well before anything changes - no surprise charges, ever.
                </p>
                <div className="font-mono-pg text-[10px] text-white/40 mt-3 uppercase tracking-widest">
                  Future pricing: $4.99 NZD / month · or $49 / year
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-px bg-white/10 mt-6">
                <button
                  onClick={() => handleUpgrade("monthly")}
                  disabled={busy === "monthly"}
                  className="bg-[#0a0a0a] hover:bg-[#1a1a1a] p-5 text-left transition-colors disabled:opacity-50"
                  data-testid="pricing-pro-monthly-btn"
                >
                  <div className="pg-overline text-white/60">Monthly</div>
                  <div className="font-heading text-3xl font-black mt-2 tracking-tighter">
                    {plans ? fmt(plans.monthly.amount) : "$4.99"}
                    <span className="text-sm font-bold text-white/60">/mo</span>
                  </div>
                  <div className="text-[11px] font-mono-pg text-white/40 mt-2 uppercase tracking-widest">
                    {busy === "monthly" ? "Loading…" : "Pay $4.99 NZD"}
                  </div>
                </button>
                <button
                  onClick={() => handleUpgrade("annual")}
                  disabled={busy === "annual"}
                  className="bg-[#0a0a0a] hover:bg-[#1a1a1a] p-5 text-left transition-colors disabled:opacity-50 relative"
                  data-testid="pricing-pro-annual-btn"
                >
                  <div className="pg-overline text-white/60">Annual</div>
                  <div className="font-heading text-3xl font-black mt-2 tracking-tighter">
                    {plans ? fmt(plans.annual.amount) : "$49"}
                    <span className="text-sm font-bold text-white/60">/yr</span>
                  </div>
                  <div className="text-[11px] font-mono-pg text-[#16A34A] mt-2 uppercase tracking-widest">
                    {busy === "annual" ? "Loading…" : "Save 2 months"}
                  </div>
                </button>
              </div>
            )}

            <ul className="mt-7 space-y-3 text-sm">
              {PRO_BULLETS.map((b) => {
                const isComingSoon = /\(coming soon\)/i.test(b);
                const Icon = isComingSoon ? Clock : ShieldCheck;
                return (
                  <li key={b} className="flex gap-2"><Icon size={18} color="#fff" weight="fill" className="shrink-0 mt-0.5" /> {b}</li>
                );
              })}
            </ul>

            {launchFree && !user && (
              <Link to="/signup" className="mt-7 inline-flex w-full justify-center bg-white text-black font-bold px-6 py-3 hover:bg-[#D97706] hover:text-white transition-colors" data-testid="pricing-pro-launch-cta">
                Sign up - Pro is free right now
              </Link>
            )}
            {launchFree && user && (
              <Link to="/dashboard" className="mt-7 inline-flex w-full justify-center border border-white/40 text-white font-bold px-6 py-3 hover:bg-white hover:text-black transition-colors" data-testid="pricing-pro-dashboard-cta">
                Open your dashboard <ArrowRight size={16} weight="bold" className="ml-2" />
              </Link>
            )}

            <p className="text-[11px] text-white/50 mt-6 font-mono-pg uppercase tracking-widest">
              {launchFree
                ? "Every signed-up user gets Pro automatically - no payment, no card on file."
                : "One-time charge - extends your Pro window. No auto-renewal."}
            </p>
          </div>

          {/* BUSINESS — short teaser in the 3-card grid. The full pitch
              lives in its own section below so it has room to breathe. */}
          <div className="bg-gradient-to-br from-[#002FA7] to-[#001A5C] text-white p-8 relative" data-testid="pricing-business-teaser">
            <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white text-[10px] font-bold tracking-widest uppercase px-2.5 py-1">
              Coming soon
            </div>
            <div className="pg-overline text-white/60">For businesses · early access</div>
            <h2 className="font-heading text-2xl font-extrabold mt-3 tracking-tight">Business</h2>
            <div className="font-heading text-4xl font-black mt-5 tracking-tighter">
              Built for your books<span className="text-[#D97706]">.</span>
            </div>

            <p className="text-sm text-white/85 leading-relaxed mt-7">
              Everything Pro can do, applied across your team, your suppliers, and your books. Warranty tracking, an automatic asset register, smarter buying, Xero and MYOB sync, spend insights, and team access - so nothing the business owns ever slips through the cracks again.
            </p>

            <a href="#business-deep-dive" className="mt-7 inline-flex items-center gap-1.5 text-sm font-bold text-white hover:text-[#D97706] transition-colors" data-testid="pricing-business-jump">
              See the full picture <ArrowRight size={14} weight="bold" />
            </a>

            <a
              href="mailto:hello@priceguard.nz?subject=PriceGuard%20Business%20-%20early%20access%20interest"
              className="mt-7 inline-flex w-full justify-center items-center gap-2 bg-[#D97706] text-white font-bold px-6 py-3 hover:bg-[#B86404] transition-colors"
              data-testid="pricing-business-cta"
            >
              Register your interest <ArrowRight size={16} weight="bold" />
            </a>

            <p className="text-[11px] text-white/60 mt-6 font-mono-pg uppercase tracking-widest">
              Pricing TBA · early-access slots reserved in order of interest
            </p>
          </div>
        </div>

        {/* BUSINESS DEEP DIVE — full pitch lives below the 3-card grid so
            it has room to breathe. Anchored at #business-deep-dive so the
            Dashboard B2B strip and the in-card "See the full picture"
            jump straight here. */}
        <BusinessDeepDive />

        {/* Trust strip - rewritten for launch phase */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]" data-testid="pricing-trust-strip">
          <div className="bg-white p-6" data-testid="trust-tile-built-in-nz">
            <div className="flex items-center gap-2 mb-2">
              <HandHeart size={18} weight="fill" color="#002FA7" />
              <span className="font-heading font-bold tracking-tight">Made in NZ for NZ shoppers</span>
            </div>
            <p className="text-sm text-[#52525b] leading-relaxed">A small Kiwi-built tool, made by Kiwis for the way New Zealand households actually shop. Designed in Aotearoa with local retailers, local policies, and local seasons in mind.</p>
          </div>
          <div className="bg-white p-6" data-testid="trust-tile-no-ads">
            <div className="flex items-center gap-2 mb-2">
              <Lock size={18} weight="fill" color="#002FA7" />
              <span className="font-heading font-bold tracking-tight">Privacy-first by design</span>
            </div>
            <p className="text-sm text-[#52525b] leading-relaxed">Your purchases and receipts are yours. We don't sell your data, ever. If we ever show offers, they'll be ours and clearly labelled - no third-party trackers running silently.</p>
          </div>
          <div className="bg-white p-6" data-testid="trust-tile-human-support">
            <div className="flex items-center gap-2 mb-2">
              <ChatCircleDots size={18} weight="fill" color="#002FA7" />
              <span className="font-heading font-bold tracking-tight">A real Kiwi reads every email</span>
            </div>
            <p className="text-sm text-[#52525b] leading-relaxed">Reply to any PriceGuard email or write to support@priceguard.nz. You'll hear back from a real Kiwi who cares about a fair go for New Zealand households.</p>
          </div>
        </div>

        <div className="mt-12">
          <FeedbackCTA
            variant="card"
            prompt="Need a different plan, retailer, or feature?"
            subject="Pricing idea / suggestion for PriceGuard NZ"
          />
        </div>

        <p className="text-xs text-[#52525b] mt-8 font-mono-pg max-w-2xl">
          By using PriceGuard NZ you accept our
          <Link to="/terms" className="underline hover:text-black mx-1" data-testid="pricing-terms-link">Terms</Link>
          and
          <Link to="/privacy" className="underline hover:text-black mx-1" data-testid="pricing-privacy-link">Privacy Policy</Link>.
        </p>
      </main>
    </div>
  );
}


// Six pillars of the Business tier - rendered as a 2x3 card grid in the
// deep-dive section. Icons are subtle, copy carries the weight.
const BUSINESS_PILLARS = [
  {
    icon: Calendar,
    title: "Fleet-wide warranty calendar",
    body: "Every device, every site, in one view. Expiry alerts go to the people responsible for ops - not just the person whose name was on the original order. When something fails, the warranty claim is drafted with the receipt, asset details and purchase date already attached, so the supplier can't ask for paperwork you don't have on hand.",
  },
  {
    icon: Database,
    title: "Asset register",
    body: "A lightweight record of every business purchase - what it was, what you paid, which retailer, when it was bought. Automatically built from receipts. Exportable for insurance, finance, or an accountant.",
  },
  {
    icon: Sparkle,
    title: "Smarter buying",
    body: "Over time PriceGuard learns when NZ retailers discount certain categories. Rather than just telling you after you've overpaid, it starts telling you when to buy - \"Harvey Norman tends to discount monitors in January, you might want to hold off.\"",
  },
  {
    icon: Plugs,
    title: "Xero and MYOB sync",
    body: "Receipts flow in automatically from your accounting software, so there's no double-handling. Savings reports are GST-friendly and ready to hand to your accountant.",
  },
  {
    icon: ChartLineUp,
    title: "Spend insights",
    body: "A dashboard showing total savings recovered, which suppliers you overpay at most, and a monthly report suitable for management or finance review.",
  },
  {
    icon: Users,
    title: "Team access",
    body: "Multiple logins, a shared receipt inbox, and role controls so the right people see the right things. Multi-location support for businesses with more than one site.",
  },
];


function BusinessDeepDive() {
  return (
    <section id="business-deep-dive" className="mt-24 -mx-6 lg:-mx-10 bg-[#0a0a0a] text-white" data-testid="business-deep-dive">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 lg:py-24">

        {/* Header */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-6" data-testid="business-tier-badge">
            <span className="bg-[#D97706] text-white text-xs font-black tracking-widest uppercase px-3 py-1.5">
              Business tier
            </span>
            <span className="text-xs text-white/60 font-mono-pg uppercase tracking-widest">
              Coming soon · for SMBs and operations teams
            </span>
          </div>
          <div className="pg-overline text-[#D97706]">The Business plan · the full picture</div>
          <h2 className="font-heading text-4xl lg:text-5xl font-black tracking-tight mt-4 leading-tight">
            Refunds and warranties shouldn't<br className="hidden lg:block" /> walk quietly out the door<span className="text-[#D97706]">.</span>
          </h2>
          <p className="text-base lg:text-lg text-white/70 leading-relaxed mt-5 max-w-2xl">
            Everything below is what the Business tier brings on top of the household Pro plan. Built for NZ businesses that buy regularly and don't have time to chase every refund or warranty themselves.
          </p>
        </div>

        {/* The problem */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <div className="pg-overline text-white/40">01 · The problem</div>
            <h3 className="font-heading text-2xl font-extrabold tracking-tight mt-3">
              Money quietly walking out the door
            </h3>
          </div>
          <div className="lg:col-span-8">
            <p className="text-base leading-relaxed text-white/80">
              Xero knows what you spent. Your email has the receipts. But once a purchase is made, nobody is watching it - checking if the price dropped, flagging when the warranty is about to expire, or telling you you've been overpaying at the same retailer for two years. For a consumer that's an inconvenience. For a business buying dozens of things a month, it's money quietly walking out the door.
            </p>
          </div>
        </div>

        {/* The gap */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start border-t border-white/10 pt-12">
          <div className="lg:col-span-4">
            <div className="pg-overline text-white/40">02 · The gap</div>
            <h3 className="font-heading text-2xl font-extrabold tracking-tight mt-3">
              No tool in NZ owns this space
            </h3>
          </div>
          <div className="lg:col-span-8">
            <p className="text-base leading-relaxed text-white/80">
              Warranty management software exists, but it's built for manufacturers, not small businesses. Receipt management tools exist, but they just store files. PriceGuard Business sits in the middle and does something neither does - it watches your purchases over time and tells you what to do about them.
            </p>
          </div>
        </div>

        {/* What it does */}
        <div className="mt-12 border-t border-white/10 pt-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start mb-10">
            <div className="lg:col-span-4">
              <div className="pg-overline text-white/40">03 · What it does</div>
              <h3 className="font-heading text-2xl font-extrabold tracking-tight mt-3">
                The full lifecycle, watched
              </h3>
            </div>
            <div className="lg:col-span-8">
              <p className="text-base leading-relaxed text-white/80">
                Everything the Pro plan does for households - daily price tracking and one-click filing of price adjustment claims - carried across the business. Then we add the things that only make sense at a business scale.
              </p>
              <p className="text-[12px] text-white/50 mt-3 font-mono-pg uppercase tracking-widest">
                And there's more to come - shaped by the businesses who get in early
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {BUSINESS_PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="bg-[#0a0a0a] p-7" data-testid={`business-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Icon size={24} weight="duotone" className="text-[#D97706]" />
                  <h4 className="font-heading text-lg font-bold tracking-tight mt-4">{p.title}</h4>
                  <p className="text-sm text-white/75 leading-relaxed mt-2">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Why it works */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start border-t border-white/10 pt-12">
          <div className="lg:col-span-4">
            <div className="pg-overline text-white/40">04 · Why it works</div>
            <h3 className="font-heading text-2xl font-extrabold tracking-tight mt-3">
              Same product, applied more broadly
            </h3>
          </div>
          <div className="lg:col-span-8">
            <p className="text-base leading-relaxed text-white/80">
              The heavy lifting is already done - PriceGuard has the receipts, the retailer knowledge, and the price tracking infrastructure. The business tier just extends that across more people, more purchases, and more of the purchase lifecycle. It's not a new product, it's the same product applied more broadly.
            </p>
          </div>
        </div>

        {/* Who it's for */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start border-t border-white/10 pt-12">
          <div className="lg:col-span-4">
            <div className="pg-overline text-white/40">05 · Who it's for</div>
            <h3 className="font-heading text-2xl font-extrabold tracking-tight mt-3">
              NZ businesses that buy regularly
            </h3>
          </div>
          <div className="lg:col-span-8">
            <p className="text-base leading-relaxed text-white/80">
              NZ small to medium businesses that buy regularly from retailers like Harvey Norman, JB Hi-Fi, Warehouse Stationery, and Noel Leeming. Office managers, finance leads, operations people - anyone responsible for keeping track of what the business owns and what it's spending.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 border-t border-white/10 pt-12 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <div className="pg-overline text-[#D97706]">Early access</div>
            <h3 className="font-heading text-3xl font-black tracking-tighter mt-3">
              Shape it with us before pricing is set<span className="text-[#D97706]">.</span>
            </h3>
            <p className="text-white/70 mt-3 max-w-2xl leading-relaxed">
              Tell us what you'd want from a business tier - the integrations that matter, the reports your accountant wants, the suppliers you wish were on the list - and we'll prioritise around real Kiwi businesses. Early-access slots are reserved in order of interest.
            </p>
          </div>
          <a
            href="mailto:hello@priceguard.nz?subject=PriceGuard%20Business%20-%20early%20access%20interest"
            className="bg-[#D97706] text-white font-bold px-6 py-3 inline-flex items-center justify-center gap-2 hover:bg-[#B86404] transition-colors whitespace-nowrap"
            data-testid="business-deep-dive-cta"
          >
            Register your interest <ArrowRight size={16} weight="bold" />
          </a>
        </div>
      </div>
    </section>
  );
}
